import { useSuspenseQuery } from '@tanstack/react-query';
import { Outlet, createFileRoute, notFound } from '@tanstack/react-router';

import { queryKeys } from '@/app/constants/queryKeys';
import { QueryParams, queryParams } from '@/app/constants/queryParams';
import { episodesDetailsQueryOptions } from '@/app/queries/episodes/episodesDetails';
import { sqids } from '@/app/utils/hashids/sqids-generator';
import { AbilityProvider } from '@/common/components/features/role-management';
import {
  StudioError,
  StudioLoader,
  StudioNotFound,
} from '@/common/components/templates/studio-page';
import { ensureNotFound } from '@/infra/network/query';
import { episodesService } from '@/modules/episodes/services';
import { musicService } from '@/modules/musics/services';
import { scriptsService } from '@/modules/scripts/services';
import { voicesService } from '@/modules/voices/services';

function RouteComponent() {
  const { id: studioIds } = Route.useParams();
  const { episodeId, podcastId, projectId } = sqids.hydrate<QueryParams.Studio>(
    studioIds,
    queryParams.studio,
  );

  const { data } = useSuspenseQuery(episodesDetailsQueryOptions(projectId, podcastId, episodeId));

  if (!data) {
    throw notFound();
  }

  return (
    <AbilityProvider role={data.role.id}>
      <Outlet />
    </AbilityProvider>
  );
}

export const Route = createFileRoute('/_app/studio/$id')({
  staleTime: Infinity,
  component: RouteComponent,
  params: {
    parse: (rawParams) => {
      const ids = sqids.decode(rawParams.id) as [number, number, number];
      return { id: ids.map((value) => value.toString()) as [string, string, string] };
    },
    stringify: (params: { id: [string, string, string] }) => {
      const [projectId, podcastId, episodeId] = params.id;

      return {
        id: sqids.encode([projectId, podcastId, episodeId]),
      };
    },
  },
  loader: async ({ params, context }) => {
    const [projectId, podcastId, episodeId] = params.id;

    context.queryClient.prefetchQuery({
      queryKey: queryKeys.voices.all,
      queryFn: () => voicesService.getVoices(),
    });

    context.queryClient.prefetchQuery({
      queryKey: queryKeys.hosts.list(projectId, podcastId, episodeId),
      queryFn: () => episodesService.getHosts({ projectId, podcastId, episodeId }),
    });

    context.queryClient.prefetchQuery({
      queryKey: queryKeys.scripts.list(episodeId),
      queryFn: () => scriptsService.getScripts({ projectId, podcastId, episodeId }),
    });

    context.queryClient.prefetchQuery({
      queryKey: queryKeys.musics.list(),
      queryFn: () => musicService.getMusics(),
    });

    return ensureNotFound(
      context.queryClient.ensureQueryData(
        episodesDetailsQueryOptions(projectId, podcastId, episodeId),
      ),
    );
  },
  errorComponent: () => <StudioError />,
  pendingComponent: () => <StudioLoader />,
  notFoundComponent: () => <StudioNotFound />,
});
